import React from 'react'
import { Close, Done, MoreHoriz, Pending } from '@mui/icons-material'
import cn from '../../../../lib/utils/cn'
import FileFormatIcon from '../../../common/FileFormatIcon'
import { shortenFileName } from '../../../../lib/utils/formatDocument'
import { translateError } from '../../../../lib/utils/translateError'

type ItemStatus =
	| 'pending'
	| 'uploading'
	| 'processing'
	| 'completed'
	| 'failed'
	| 'canceled'

interface ProgressItemProps<T> {
	item: T & {
		id: string
		name?: string
		status: ItemStatus
		error?: string
		progress?: number
		type?: number
	}
	index: number
	itemKey: string
	displayName: string
	isFolder: boolean
	isLoading: boolean
	showProgressBar?: boolean
	filterTechnicalErrors?: boolean
	renderCustomContent?: (item: T, index: number) => React.ReactNode
	renderActions?: (item: T, index: number) => React.ReactNode
}

const ProgressItem = <T,>({
	item,
	index,
	itemKey,
	displayName,
	isFolder,
	isLoading,
	showProgressBar = false,
	filterTechnicalErrors = false,
	renderCustomContent,
	renderActions,
}: ProgressItemProps<T>) => {
	const getErrorMessage = () => {
		if (!item.error) return null

		if (filterTechnicalErrors) {
			const isTechnicalError =
				item.error.includes('status code') ||
				item.error.includes('Request failed') ||
				item.error.includes('Network Error') ||
				item.error.includes('timeout')

			if (isTechnicalError) {
				return null
			}
		}

		const translatedError = translateError(item.error)
		if (translatedError) {
			return translatedError
		}

		if (item.error === 'OBJECTS_MUST_BE_FROM_SAME_PARENT') {
			return 'Files must be from the same folder'
		}

		return item.error
	}

	const renderIcon = (status: ItemStatus) => {
		switch (status) {
			case 'uploading':
			case 'processing': {
				return (
					<div className="w-6 h-6 flex items-center justify-center bg-text-secondary rounded-full">
						<MoreHoriz className="w-4 h-4 fill-white" />
					</div>
				)
			}
			case 'completed': {
				return (
					<div className="w-6 h-6 flex items-center justify-center bg-positive rounded-full">
						<Done className="w-4 h-4 fill-white" />
					</div>
				)
			}
			case 'failed': {
				const errorMessage = getErrorMessage()
				return (
					<div className="group/icon">
						<div className="w-6 h-6 flex items-center justify-center bg-negative rounded-full group-hover/icon:hidden">
							<Close className="w-4 h-4 fill-white" />
						</div>
						<div className="hidden group-hover/icon:flex absolute rounded-lg z-10 items-center justify-start px-[14px] w-[calc(100%-28px)] h-10 left-0 top-0 bg-negative">
							<Close className="w-4 h-4 fill-white flex-shrink-0" />
							<p className="text-white ml-4 text-nowrap ellipsis max-w-full">
								{errorMessage || 'Failed'}
							</p>
						</div>
					</div>
				)
			}
			case 'canceled': {
				const errorMessage = getErrorMessage()
				return (
					<div className="group/icon">
						<div className="w-6 h-6 flex items-center justify-center bg-text-secondary rounded-full group-hover/icon:hidden">
							<Close className="w-4 h-4 fill-white" />
						</div>
						<div className="hidden group-hover/icon:flex absolute rounded-lg z-10 items-center justify-start px-[14px] w-[calc(100%-28px)] h-10 left-0 top-0 bg-text-secondary">
							<Close className="w-4 h-4 fill-white flex-shrink-0" />
							<p className="text-white ml-4 text-nowrap ellipsis max-w-full">
								{errorMessage || 'Canceled'}
							</p>
						</div>
					</div>
				)
			}
			default:
				return (
					<div className="w-6 h-6 flex items-center justify-center bg-accent rounded-full">
						<Pending className="w-4 h-4 fill-white animate-pulse" />
					</div>
				)
		}
	}

	return (
		<div
			key={itemKey}
			className={cn(
				'w-full box-border relative flex items-center justify-start rounded-lg bg-background-light dark:bg-background-dark px-2.5 min-h-10 h-10 py-1',
				isLoading && 'bg-background-light dark:bg-background-dark'
			)}
		>
			<div className="mr-3">{renderIcon(item.status)}</div>

			<div className="p-1 pl-0 w-2/3 flex flex-col items-start justify-start ellipsis">
				<p className="text-base flex gap-1 items-center text-text-main w-full ellipsis dark:text-text-main-dark">
					<FileFormatIcon
						fileName={displayName}
						isFolder={isFolder}
						size={20}
					/>
					<span className="w-full text-start text-nowrap ellipsis">
						{shortenFileName(displayName, 20)}
					</span>
				</p>

				{renderCustomContent?.(item as T, index)}

				{isLoading && showProgressBar && item.progress !== undefined && (
					<div className="h-1 w-full mt-1 bg-background-light dark:bg-background-dark rounded overflow-hidden">
						<div
							style={{
								width: `${item.progress}%`,
							}}
							className="bg-accent h-full rounded"
						/>
					</div>
				)}
			</div>
			{renderActions && (
				<div className="ml-auto">{renderActions(item as T, index)}</div>
			)}
		</div>
	)
}

export default ProgressItem
